import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Database,
  Filter,
  GitBranch,
  History,
  Info,
  Layers,
  Loader2,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Tags,
  Target,
  X,
  Zap
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';

const VERSION_LABEL_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/;
const MICRO_POINT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const configurationSections = [
  {
    key: 'matching',
    label: 'Matching Policy',
    description: 'Weights, retrieval, LLM guardrails, and release notes.',
    icon: SlidersHorizontal
  },
  {
    key: 'microPoints',
    label: 'Micro-points',
    description: 'Category multipliers and individual evidence controls.',
    icon: Tags
  },
  {
    key: 'data',
    label: 'Data Inventory',
    description: 'Matching source coverage and operational readiness.',
    icon: Database
  },
  {
    key: 'compliance',
    label: 'KYC & Taxonomy',
    description: 'Regional verification and canonical taxonomy health.',
    icon: ClipboardCheck
  },
  {
    key: 'history',
    label: 'History',
    description: 'Version restore, audit posture, and release guardrails.',
    icon: History
  }
];

const fallbackEngineControls = [
  { key: 'llmMaxAdjustment', label: 'LLM maximum adjustment', unit: 'points', min: 0, max: 50, step: 0.5, defaultValue: 15 },
  { key: 'stage3ScoreSpreadThreshold', label: 'Stage 3 score spread threshold', unit: 'points', min: 0, max: 100, step: 0.5, defaultValue: 25 },
  { key: 'stage3TopN', label: 'Stage 3 candidate limit', unit: 'candidates', min: 5, max: 200, step: 1, defaultValue: 10 },
  { key: 'llmMinCandidates', label: 'LLM minimum candidates', unit: 'candidates', min: 2, max: 100, step: 1, defaultValue: 5 },
  { key: 'minCompositeScore', label: 'Minimum composite score', unit: 'points', min: 0, max: 100, step: 0.5, defaultValue: 20 },
  { key: 'vectorWeight', label: 'Vector blend weight', unit: 'percent', min: 0, max: 100, step: 0.5, defaultValue: 45 },
  { key: 'vectorWeightFloor', label: 'Vector weight floor', unit: 'percent', min: 0, max: 30, step: 0.5, defaultValue: 5 },
  { key: 'lexicalWeight', label: 'Lexical blend weight', unit: 'percent', min: 0, max: 50, step: 0.5, defaultValue: 15 },
  { key: 'bm25OnlyWeight', label: 'BM25-only weight', unit: 'percent', min: 0, max: 35, step: 0.5, defaultValue: 12 },
  { key: 'rrfK', label: 'RRF smoothing constant', unit: 'rank', min: 1, max: 500, step: 1, defaultValue: 60 }
];

const fallbackSemanticSections = [
  { key: 'skills', label: 'Skills embeddings', description: 'Dense-vector similarity for skill evidence.', defaultPercent: 35 },
  { key: 'experience', label: 'Experience embeddings', description: 'Dense-vector similarity for work-history evidence.', defaultPercent: 25 },
  { key: 'education', label: 'Education embeddings', description: 'Dense-vector similarity for academic and credential evidence.', defaultPercent: 10 },
  { key: 'summary', label: 'Profile summary embeddings', description: 'Compact cross-functional profile similarity.', defaultPercent: 10 },
  { key: 'full', label: 'Full profile embeddings', description: 'Whole-profile semantic similarity.', defaultPercent: 20 }
];

const fallbackRrfChannels = [
  { key: 'deterministic', label: 'Deterministic rank', description: 'Structured scoring rank contribution.', defaultPercent: 50 },
  { key: 'semantic', label: 'Semantic rank', description: 'Dense retrieval rank contribution.', defaultPercent: 35 },
  { key: 'lexical', label: 'Lexical rank', description: 'BM25 exact-term rank contribution.', defaultPercent: 15 }
];

const explanationCopy = {
  skills: {
    title: 'Skills Weight',
    summary: 'Controls how much canonical skill and requirement tag overlap influences deterministic candidate scoring.',
    impact: 'A higher value pushes candidates with stronger required and preferred skill evidence upward across jobs that use the global matching policy.',
    guidance: 'Increase for roles where day-one capability is critical. Decrease when broader potential, trainability, or adjacent experience should matter more.',
    risk: 'If set too high, strong candidates with transferable skills can be under-ranked. If set too low, rankings may surface candidates without enough practical capability.'
  },
  experience: {
    title: 'Experience Weight',
    summary: 'Controls the impact of seniority, years, role scope, industry context, and work-history evidence.',
    impact: 'A higher value favors candidates who have already operated in similar responsibility, domain, or stakeholder environments.',
    guidance: 'Increase for senior, regulated, client-facing, or high-autonomy roles. Decrease for internships, junior roles, or roles where skills evidence matters more.',
    risk: 'Over-weighting experience can disadvantage emerging talent. Under-weighting it can over-promote candidates who meet skills keywords but lack operating context.'
  },
  education: {
    title: 'Education Weight',
    summary: 'Controls how formal education evidence contributes to deterministic ranking.',
    impact: 'A higher value gives more influence to degree level, field relevance, institution context, GPA/classification, and transcript evidence.',
    guidance: 'Increase for licensed, academic, technical graduate, or compliance-heavy pipelines. Decrease where portfolios and work evidence should dominate.',
    risk: 'Over-weighting education can reduce opportunity for non-traditional candidates. Under-weighting it can weaken screening for roles that require formal credentials.'
  },
  microPoints: {
    title: 'Micro-points Weight',
    summary: 'Controls the influence of detailed evidence signals underneath the broad scoring dimensions.',
    impact: 'A higher value lets evidence such as portfolio strength, research depth, language use, regulated exposure, and project impact separate similar candidates.',
    guidance: 'Increase when nuanced evidence should distinguish candidate quality. Decrease when the broad dimensions should remain the primary ranking drivers.',
    risk: 'Over-weighting micro-points can make rankings harder to explain. Under-weighting them can make the system miss high-signal details.'
  },
  location: {
    title: 'Location Weight',
    summary: 'Controls the impact of location, remote readiness, relocation, authorization, and regional fit.',
    impact: 'A higher value favors candidates who are more operationally compatible with the job location and work arrangement.',
    guidance: 'Increase for onsite roles, authorization-sensitive roles, or time-zone-bound work. Decrease for flexible, global, or fully remote hiring.',
    risk: 'Over-weighting location can reduce candidate pool diversity. Under-weighting it can create late-stage availability and authorization mismatches.'
  },
  salary: {
    title: 'Salary Weight',
    summary: 'Controls how compensation alignment affects candidate ranking.',
    impact: 'A higher value favors candidates whose stated expectations align with configured job compensation ranges.',
    guidance: 'Increase when budget fit is strict. Decrease when compensation is negotiable or intentionally handled later in the process.',
    risk: 'Over-weighting salary can suppress candidates worth negotiating with. Under-weighting it can create downstream offer friction.'
  },
  llmMaxAdjustment: {
    title: 'LLM Maximum Adjustment',
    summary: 'Limits how far the LLM re-ranking stage may move a candidate score after deterministic and retrieval scoring.',
    impact: 'A higher limit gives the LLM more room to correct close rankings using retrieved evidence. A lower limit keeps final scores closer to deterministic output.',
    guidance: 'Keep this conservative. Increase only when evidence quality is strong and review teams accept more LLM influence.',
    risk: 'Too high can introduce unstable ranking movement. Too low can prevent the LLM from correcting clear close-call ordering issues.'
  },
  stage3ScoreSpreadThreshold: {
    title: 'Stage 3 Score Spread Threshold',
    summary: 'Controls when LLM re-ranking runs by checking whether the top candidate pool is close enough to justify additional review.',
    impact: 'A higher threshold runs LLM re-ranking more often. A lower threshold reserves it for very close candidate groups.',
    guidance: 'Increase when precision is more important than cost. Decrease when deterministic scoring is sufficient or AI cost must be tightly controlled.',
    risk: 'Too high can increase AI expense and latency. Too low can skip useful re-ranking in competitive pools.'
  },
  stage3TopN: {
    title: 'Stage 3 Candidate Limit',
    summary: 'Controls how many candidates are sent to the LLM re-ranking step after hybrid scoring.',
    impact: 'A higher value broadens LLM review. A lower value reduces latency and cost.',
    guidance: 'Keep the value focused on the competitive head of the ranking. Very large values should be backed by clear monitoring.',
    risk: 'Too high can increase cost and slow rematches. Too low can miss close candidates just outside the head of the list.'
  },
  llmMinCandidates: {
    title: 'LLM Minimum Candidates',
    summary: 'Sets the minimum candidate count required before LLM re-ranking can run.',
    impact: 'This prevents LLM cost when the pool is too small for re-ranking to add much value.',
    guidance: 'Use a value that reflects the minimum competitive shortlist size for most jobs.',
    risk: 'Too low spends AI budget on weak pools. Too high skips re-ranking for legitimate smaller pipelines.'
  },
  minCompositeScore: {
    title: 'Minimum Composite Score',
    summary: 'Candidates below this hybrid score are excluded from persisted matches.',
    impact: 'A higher value improves displayed match quality but reduces recall. A lower value increases recall but may create noisy results.',
    guidance: 'Adjust only after reviewing match quality, ghosting, and employer shortlist behavior.',
    risk: 'Too high can hide viable candidates. Too low can flood employers with weak matches.'
  },
  vectorWeight: {
    title: 'Vector Blend Weight',
    summary: 'Maximum share of Stage 2 hybrid score contributed by dense semantic retrieval.',
    impact: 'Higher values favor candidates whose profile meaning aligns with the job even when exact tags differ.',
    guidance: 'Increase when embeddings and extraction coverage are strong. Decrease when structured requirements should dominate.',
    risk: 'Too high can make rankings harder to explain. Too low can miss transferable or semantically adjacent candidates.'
  },
  vectorWeightFloor: {
    title: 'Vector Weight Floor',
    summary: 'Minimum vector contribution when embeddings exist but extraction confidence is weak.',
    impact: 'Keeps semantic evidence from collapsing to zero because of imperfect section confidence.',
    guidance: 'Keep this low. It is a guardrail, not the primary retrieval weight.',
    risk: 'Too high can over-trust weak embeddings. Too low can discard useful semantic evidence during partial extraction failures.'
  },
  lexicalWeight: {
    title: 'Lexical Blend Weight',
    summary: 'Share of semantic retrieval contributed by BM25 exact-term matching when dense vectors are present.',
    impact: 'A higher value rewards exact language overlap such as certifications, tools, locations, and domain terms.',
    guidance: 'Increase for credential-heavy or exact-skill roles. Decrease when synonyms and broader semantic similarity matter more.',
    risk: 'Too high can recreate keyword matching. Too low can miss exact required terms.'
  },
  bm25OnlyWeight: {
    title: 'BM25-only Weight',
    summary: 'Fallback retrieval weight when lexical evidence exists but dense embeddings are unavailable.',
    impact: 'Controls how much exact-term evidence can influence scores without embeddings.',
    guidance: 'Keep below the normal vector blend unless BM25 has proven reliable in fallback scenarios.',
    risk: 'Too high can over-rank keyword-heavy profiles. Too low can ignore useful fallback evidence.'
  },
  rrfK: {
    title: 'RRF Smoothing Constant',
    summary: 'Controls how strongly reciprocal rank fusion reacts to rank differences.',
    impact: 'Higher values reduce sensitivity to small rank differences. Lower values make top positions more influential.',
    guidance: 'Change carefully because it affects how deterministic, semantic, and lexical rank signals combine.',
    risk: 'Too low can make rankings volatile. Too high can flatten meaningful rank differences.'
  },
  semanticSectionWeights: {
    title: 'Semantic Section Weights',
    summary: 'Controls how dense-vector similarities from skills, experience, education, summary, and full profile combine.',
    impact: 'These weights affect retrieval quality before final hybrid scoring.',
    guidance: 'Keep the total at 100%. Increase sections with strong extraction quality and high relevance for your market.',
    risk: 'Bad section balance can over-promote candidates with strong summaries but weak required evidence, or the reverse.'
  },
  rrfWeights: {
    title: 'Rank Fusion Weights',
    summary: 'Controls how deterministic rank, semantic rank, and lexical rank are combined.',
    impact: 'These weights affect ordering among candidates after the main score is calculated.',
    guidance: 'Keep the total at 100%. Use deterministic weight for explainability, semantic for recall, and lexical for exact evidence.',
    risk: 'Over-weighting one channel can hide useful signals from the other channels.'
  },
  microPointCategories: {
    title: 'Micro-point Category Multipliers',
    summary: 'Controls category-level multipliers for detailed evidence requirements generated from jobs.',
    impact: 'A disabled category stops related micro-requirements from being created. A multiplier changes their generated weight.',
    guidance: 'Use category controls for broad regional or product-level policy shifts, then use micro-point controls for precise exceptions.',
    risk: 'Disabling a category can remove important evidence from matching. High multipliers can overstate narrow signals.'
  },
  microPointWeights: {
    title: 'Individual Micro-point Weights',
    summary: 'Controls each fine-grained matching signal, including enablement, weight, minimum score, and mandatory treatment.',
    impact: 'These settings affect future job micro-requirements and the micro-point contribution inside deterministic scoring.',
    guidance: 'Use precise changes with clear monitoring. Prefer changing one family of points at a time.',
    risk: 'Aggressive overrides can make rankings hard to explain or create hidden eligibility constraints.'
  },
  dataPoints: {
    title: 'Matching Data Points',
    summary: 'Shows the source tables and evidence families that feed matching and scoring.',
    impact: 'Data availability affects whether a configuration can have measurable impact.',
    guidance: 'Use this inventory to understand which controls are backed by live evidence before increasing their weight.',
    risk: 'Raising weights for sparse data can produce unstable rankings and weak audit explanations.'
  },
  dimensionTotal: {
    title: 'Dimension Total',
    summary: 'The global matching policy requires broad dimensions to total exactly 100%.',
    impact: 'The matching engines normalize weights internally, but enforcing 100% keeps the admin policy readable, auditable, and predictable.',
    guidance: 'When one dimension increases, reduce one or more other dimensions so total influence stays balanced.',
    risk: 'Saving ambiguous totals makes audit review harder and increases the chance that operators misunderstand relative influence.'
  },
  versioning: {
    title: 'Versioned Configuration',
    summary: 'Every save creates a new active global matching policy instead of overwriting the previous row.',
    impact: 'Future matching runs use the newest active global policy unless an employer-level or job-level override applies. Historical rows remain available for audit and restore.',
    guidance: 'Use specific version labels and change reasons. Treat this like a production release, because rankings across the platform can change.',
    risk: 'Unexplained changes make hiring outcomes difficult to defend. Restoring a prior version should also include a clear reason.'
  },
  kycPolicy: {
    title: 'Regional KYC Requirements',
    summary: 'Regional document policies determine what employers must upload before verification review.',
    impact: 'Requirement changes affect future submissions. In-flight reviews remain locked to their captured policy snapshot.',
    guidance: 'Manage document requirements from the KYC Verification area and keep regional legal and compliance requirements up to date.',
    risk: 'Incorrect requirements can block legitimate employers or approve accounts without enough compliance evidence.'
  },
  taxonomy: {
    title: 'Taxonomy Governance',
    summary: 'The taxonomy controls canonical tags and micro-point definitions used by extraction and matching.',
    impact: 'Pending suggestions and active micro-points influence how job requirements and candidate evidence are normalized.',
    guidance: 'Review high-confidence suggestions regularly and avoid approving duplicate or vague tags.',
    risk: 'Poor taxonomy hygiene creates inconsistent matching and weak explanations.'
  }
};

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  } catch (_error) {
    return String(value);
  }
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, toNumber(value, min)));

const stableObject = (value) => {
  if (Array.isArray(value)) return value.map(stableObject);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableObject(value[key]);
    return result;
  }, {});
};

const isVersionLabelValid = (value = '') => {
  const trimmed = String(value || '').trim();
  return !trimmed || (trimmed.length >= 4 && trimmed.length <= 80 && VERSION_LABEL_PATTERN.test(trimmed));
};

const objectFromDefinitions = (definitions, sourceValues, valueKey = 'weightPercent') => (
  (definitions || []).reduce((result, definition) => {
    result[definition.key] = toNumber(
      sourceValues?.[definition.key] ?? definition[valueKey],
      definition.defaultPercent ?? 0
    );
    return result;
  }, {})
);

const buildDraft = (active, matching = {}) => {
  const dimensionWeights = {};
  (active?.dimensions || []).forEach((dimension) => {
    dimensionWeights[dimension.payloadKey] = toNumber(dimension.weightPercent, dimension.defaultPercent);
  });

  const engineControls = {};
  const engineDefinitions = active?.engineControlDefinitions || matching?.definitions?.engineControls || fallbackEngineControls;
  engineDefinitions.forEach((definition) => {
    engineControls[definition.key] = toNumber(
      active?.engineControls?.[definition.key],
      definition.defaultValue
    );
  });

  const semanticDefinitions = active?.semanticSections || matching?.definitions?.semanticSections || fallbackSemanticSections;
  const rrfDefinitions = active?.rrfChannels || matching?.definitions?.rankFusion || fallbackRrfChannels;

  const categoryWeights = {};
  const categories = matching?.microPointCategories?.length
    ? matching.microPointCategories
    : Object.entries(active?.categoryWeights || {}).map(([key, value]) => ({ key, ...value }));
  categories.forEach((category) => {
    categoryWeights[category.key] = {
      enabled: category.enabled !== false,
      weight: toNumber(category.weightPercent ?? category.weight, 100)
    };
  });

  const microPointWeights = {};
  const microPoints = matching?.microPoints?.length
    ? matching.microPoints
    : Object.entries(active?.microPointWeights || {}).map(([key, value]) => ({ key, ...value }));
  microPoints.forEach((point) => {
    microPointWeights[point.key] = {
      enabled: point.enabled !== false,
      weight: toNumber(point.weightPercent ?? point.weight, point.defaultWeightPercent ?? 10),
      minimumScore: point.minimumScore === null || point.minimumScore === undefined ? null : toNumber(point.minimumScore),
      mandatory: point.mandatory === true
    };
  });

  return {
    versionLabel: '',
    changeReason: '',
    dimensionWeights,
    engineControls,
    semanticSectionWeights: objectFromDefinitions(semanticDefinitions, active?.semanticSectionWeights),
    rrfWeights: objectFromDefinitions(rrfDefinitions, active?.rrfWeights),
    categoryWeights,
    microPointWeights
  };
};

const draftComparable = (draft) => stableObject({
  dimensionWeights: draft?.dimensionWeights || {},
  engineControls: draft?.engineControls || {},
  semanticSectionWeights: draft?.semanticSectionWeights || {},
  rrfWeights: draft?.rrfWeights || {},
  categoryWeights: draft?.categoryWeights || {},
  microPointWeights: draft?.microPointWeights || {}
});

const serializeDraftForCompare = (draft) => JSON.stringify(draftComparable(draft));

const serializePolicyForCompare = (policy) => serializeDraftForCompare({
  dimensionWeights: (policy?.dimensions || []).reduce((result, dimension) => {
    result[dimension.payloadKey] = toNumber(dimension.weightPercent);
    return result;
  }, {}),
  engineControls: policy?.engineControls || {},
  semanticSectionWeights: policy?.semanticSectionWeights || {},
  rrfWeights: policy?.rrfWeights || {},
  categoryWeights: policy?.categoryWeights || {},
  microPointWeights: policy?.microPointWeights || {}
});

const isPolicyTotalValid = (policy) => Math.abs(toNumber(policy?.totalDimensionPercent) - 100) <= 0.1;

const ConfigurationsSkeleton = () => (
  <div className="min-h-screen bg-[#f8f9fa] p-4 sm:p-6 lg:p-8" aria-label="Loading platform configurations">
    <div className="mx-auto max-w-[1400px] space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <SkeletonBlock className="h-4 w-44 rounded-md" />
          <SkeletonBlock className="h-9 w-64 rounded-lg" />
          <SkeletonBlock className="h-4 w-[42rem] max-w-full rounded-md" />
        </div>
        <SkeletonBlock className="h-10 w-28 rounded-lg" />
      </header>
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => <SkeletonBlock key={index} className="h-36 rounded-lg" />)}
      </section>
      <section className="rounded-[24px] border border-slate-200 bg-white p-2 shadow-sm">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => <SkeletonBlock key={index} className="h-32 rounded-lg" />)}
        </div>
      </section>
      <section className="space-y-6">
        <SkeletonBlock className="h-32 rounded-xl" />
        <section className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <SkeletonBlock className="mb-5 h-20 rounded-lg" />
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-lg" />)}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-lg" />)}
          </div>
        </section>
      </section>
    </div>
  </div>
);

const InfoModal = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section role="dialog" aria-modal="true" aria-label={item.title} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-900">Configuration explanation</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-900">{item.title}</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 p-5 text-sm leading-6 text-slate-600">
          <div>
            <h3 className="text-sm font-semibold text-[#0b1f3a]">What it controls</h3>
            <p className="mt-1">{item.summary}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#0b1f3a]">Platform impact</h3>
            <p className="mt-1">{item.impact}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#0b1f3a]">Operational guidance</h3>
            <p className="mt-1">{item.guidance}</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p><span className="font-semibold">Risk: </span>{item.risk}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const InfoButton = ({ itemKey, item, onOpen }) => {
  const explanation = item || explanationCopy[itemKey];
  if (!explanation) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(explanation)}
      className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-400 hover:text-slate-900"
      aria-label={`Explain ${explanation.title || 'configuration'}`}
    >
      <Info size={12} />
    </button>
  );
};

const Panel = ({ icon: Icon, title, subtitle, action, children }) => (
  <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm flex flex-col overflow-hidden">
    <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-start sm:justify-between bg-white">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-blue-100/50 bg-blue-50 text-blue-600">
            <Icon size={20} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold leading-tight text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const Metric = ({ icon: Icon, label, value, detail, infoKey, onInfo, tone = 'default' }) => {
  const cardClass = tone === 'danger' ? 'border-rose-200' : tone === 'warning' ? 'border-amber-200' : 'border-slate-100';
  return (
    <article className={`bg-white rounded-[24px] p-6 shadow-sm border ${cardClass} flex flex-col justify-between hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-6">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        <div className="flex items-center gap-2">
          {infoKey && <InfoButton itemKey={infoKey} onOpen={onInfo} />}
          {Icon && (
            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100/50 shrink-0">
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
      </div>
      <div>
        <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
        {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
      </div>
    </article>
  );
};

const SectionNav = ({ activeSection, onChange, counts }) => (
  <nav className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-1 shadow-sm" aria-label="Configuration sections">
    <div className="flex min-w-max gap-1">
      {configurationSections.map((section) => {
        const Icon = section.icon;
        const active = activeSection === section.key;
        const count = counts?.[section.key];
        return (
          <button
            key={section.key}
            type="button"
            onClick={() => onChange(section.key)}
            aria-pressed={active}
            className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium transition ${
              active ? 'bg-slate-100 text-slate-900 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
          >
            <Icon size={16} />
            <span>{section.label}</span>
            {count !== undefined && (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                active ? 'bg-white text-slate-900' : 'bg-slate-100 text-slate-500'
              }`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </nav>
);

const SectionIntro = ({ icon: Icon, eyebrow, title, subtitle, action }) => (
  <div className="flex flex-col gap-4 rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between">
    <div className="flex min-w-0 items-start gap-4">
      {Icon && (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-blue-100/50 bg-blue-50 text-blue-600">
          <Icon size={24} />
        </span>
      )}
      <div className="min-w-0">
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</p>}
        <h2 className="mt-1 text-xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const PaginationControls = ({ page, pageSize, totalItems, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems ? (page * pageSize) + 1 : 0;
  const end = Math.min(totalItems, (page + 1) * pageSize);

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-800">{start}-{end}</span> of <span className="font-semibold text-slate-800">{totalItems}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-500">
          Rows
          <select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
          >
            {MICRO_POINT_PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page <= 0}
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={15} /> Prev
        </button>
        <span className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">{page + 1} / {totalPages}</span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

const TotalStatus = ({ label, value, valid, infoKey, onInfo }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <div className="flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]">
        {label}
        <InfoButton itemKey={infoKey} onOpen={onInfo} />
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-500">Total must equal 100%. Current total: {value.toFixed(1)}%.</p>
    </div>
    <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${valid ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
      {valid ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
      {valid ? 'Balanced' : 'Needs adjustment'}
    </span>
  </div>
);

const NumberControl = ({ label, description, value, min, max, step = 1, suffix = '', infoKey, infoItem, onChange, onInfo, soft = false }) => (
  <div className={`rounded-lg border border-slate-200 p-4 ${soft ? 'bg-slate-50' : 'bg-white'}`}>
    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#0b1f3a]">{label}</span>
          <InfoButton itemKey={infoKey} item={infoItem} onOpen={onInfo} />
        </div>
        {description && <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>}
      </div>
      <span className="rounded-lg bg-sky-50 px-2.5 py-1 text-sm font-semibold text-sky-700">{toNumber(value).toFixed(step < 1 ? 1 : 0)}{suffix}</span>
    </div>
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="min-w-0 flex-1 accent-sky-600"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-10 w-24 shrink-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
      />
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition ${checked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-500'}`}
  >
    <span className={`h-2 w-2 rounded-full ${checked ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {label}
  </button>
);

const CategoryControl = ({ category, value, onChange, onInfo }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-[#0b1f3a]">{category.label || category.key}</p>
          <InfoButton
            item={{
              title: `${category.label || category.key} Category`,
              summary: category.description || 'Controls a micro-point category multiplier.',
              impact: 'The multiplier changes generated job micro-requirement weights for this category. Disabling it prevents the category from being applied.',
              guidance: 'Use category controls for broad adjustments, then use individual micro-point controls for precise behavior.',
              risk: 'Large category changes can shift ranking behavior across many jobs at once.'
            }}
            onOpen={onInfo}
          />
        </div>
        <p className="mt-1 text-xs leading-5 text-slate-500">{category.description}</p>
        <p className="mt-2 text-xs text-slate-400">{category.activeMicroPointCount || 0} active points, {category.jobRequirementCount || 0} job requirements</p>
      </div>
      <Toggle checked={value.enabled !== false} onChange={(checked) => onChange({ ...value, enabled: checked })} label={value.enabled !== false ? 'Enabled' : 'Disabled'} />
    </div>
    <div className="mt-4 flex items-center gap-3">
      <input
        type="range"
        min="0"
        max="200"
        step="1"
        value={value.weight}
        disabled={value.enabled === false}
        onChange={(event) => onChange({ ...value, weight: Number(event.target.value) })}
        className="min-w-0 flex-1 accent-sky-600 disabled:opacity-40"
      />
      <div className="relative w-24 shrink-0">
        <input
          type="number"
          min="0"
          max="200"
          step="1"
          value={value.weight}
          disabled={value.enabled === false}
          onChange={(event) => onChange({ ...value, weight: clamp(event.target.value, 0, 200) })}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 pr-8 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-50"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
      </div>
    </div>
  </div>
);

const MicroPointControl = ({ point, value, onChange, onInfo }) => {
  const explanation = {
    title: point.displayName || point.key,
    summary: point.description || 'Controls this micro-point matching signal.',
    impact: `Category: ${point.category}. Used by ${point.jobRequirementCount || 0} job requirements and ${point.candidateEvidenceCount || 0} candidate evidence rows.`,
    guidance: 'Use the weight for relative contribution, minimum score for eligibility strength, mandatory only when failing the point should materially affect the match.',
    risk: 'High weights or mandatory settings can create hidden constraints if the underlying data is sparse or inconsistently extracted.'
  };

  return (
    <div className={`rounded-lg border p-4 ${value.enabled === false ? 'border-slate-200 bg-slate-50 opacity-80' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-[#0b1f3a]">{point.displayName || point.key}</p>
            <InfoButton item={explanation} onOpen={onInfo} />
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{point.category}</span>
            {!point.isActive && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Inactive definition</span>}
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">{point.description}</p>
          <p className="mt-2 text-xs text-slate-400">
            Key: {point.key} · Default {toNumber(point.defaultWeightPercent).toFixed(1)}% · {point.scoringMethod || 'deterministic'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle checked={value.enabled !== false} onChange={(checked) => onChange({ ...value, enabled: checked })} label={value.enabled !== false ? 'Enabled' : 'Disabled'} />
          <Toggle checked={value.mandatory === true} onChange={(checked) => onChange({ ...value, mandatory: checked })} label={value.mandatory ? 'Mandatory' : 'Optional'} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_160px_160px]">
        <div>
          <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600">
            <span>Weight</span>
            <span>{toNumber(value.weight).toFixed(1)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="0.5"
            value={value.weight}
            disabled={value.enabled === false}
            onChange={(event) => onChange({ ...value, weight: Number(event.target.value) })}
            className="w-full accent-sky-600 disabled:opacity-40"
          />
        </div>
        <label className="block text-xs font-semibold text-slate-600">
          Weight %
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={value.weight}
            disabled={value.enabled === false}
            onChange={(event) => onChange({ ...value, weight: clamp(event.target.value, 0, 100) })}
            className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-50"
          />
        </label>
        <label className="block text-xs font-semibold text-slate-600">
          Minimum score
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="None"
            value={value.minimumScore ?? ''}
            disabled={value.enabled === false}
            onChange={(event) => onChange({
              ...value,
              minimumScore: event.target.value === '' ? null : clamp(event.target.value, 0, 100)
            })}
            className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-50"
          />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4">
        <span className="rounded-md bg-slate-50 p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{point.candidateEvidenceCount || 0}</strong>Candidate facts</span>
        <span className="rounded-md bg-slate-50 p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{point.jobRequirementCount || 0}</strong>Job requirements</span>
        <span className="rounded-md bg-slate-50 p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{point.requiredJobRequirementCount || 0}</strong>Required uses</span>
        <span className="rounded-md bg-slate-50 p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{point.avgMinimumScore ?? 'None'}</strong>Avg minimum</span>
      </div>
    </div>
  );
};

const RestoreModal = ({ config, saving, onClose, onConfirm }) => {
  const [form, setForm] = useState({ versionLabel: '', changeReason: '' });
  if (!config) return null;
  const versionLabelValid = isVersionLabelValid(form.versionLabel);
  const valid = form.changeReason.trim().length >= 20 && versionLabelValid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section className="w-full max-w-xl rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Restore policy</p>
            <h2 className="mt-1 text-xl font-semibold text-[#0b1f3a]">{config.versionLabel}</h2>
            <p className="mt-1 text-sm text-slate-500">This will create a new active global policy using the selected version's weights.</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <label className="block text-sm font-semibold text-slate-700">
            New version label <span className="font-normal text-slate-400">(optional)</span>
            <input
              value={form.versionLabel}
              onChange={(event) => setForm((current) => ({ ...current, versionLabel: event.target.value }))}
              placeholder={`restore-${config.id}`}
              aria-invalid={!versionLabelValid}
              className="mt-2 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm font-normal outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
            {!versionLabelValid && (
              <span className="mt-1 block text-xs font-normal text-rose-600">
                Use 4-80 characters. Start with a letter or number; only dot, underscore, colon, and hyphen are allowed.
              </span>
            )}
          </label>
          <label className="block text-sm font-semibold text-slate-700">
            Restore reason
            <textarea
              value={form.changeReason}
              onChange={(event) => setForm((current) => ({ ...current, changeReason: event.target.value }))}
              rows={4}
              maxLength={1200}
              placeholder="Explain why this global policy version is being restored."
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </label>
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 p-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button
            type="button"
            disabled={!valid || saving}
            onClick={() => onConfirm({
              changeReason: form.changeReason.trim(),
              ...(form.versionLabel.trim() ? { versionLabel: form.versionLabel.trim() } : {})
            })}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#12345c] disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <RotateCcw size={16} />}
            Restore as new active version
          </button>
        </div>
      </section>
    </div>
  );
};

function ConfigurationsPage() {
  const queryClient = useQueryClient();
  const draftSourceRef = useRef(null);
  const [activeSection, setActiveSection] = useState('matching');
  const [infoItem, setInfoItem] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restoreConfig, setRestoreConfig] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [policyConflict, setPolicyConflict] = useState(false);
  const [microSearch, setMicroSearch] = useState('');
  const [microCategory, setMicroCategory] = useState('all');
  const [microPage, setMicroPage] = useState(0);
  const [microPageSize, setMicroPageSize] = useState(20);

  const configQuery = useQuery('admin-configurations', adminAPI.getConfigurations, {
    refetchInterval: 90000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false
  });

  const data = configQuery.data || {};
  const matching = data.matching;
  const active = matching?.active;
  const usage = matching?.usage || {};
  const history = matching?.history || [];
  const kyc = data.kyc || {};
  const taxonomy = data.taxonomy || {};
  const microPointSummary = matching?.microPointSummary || {};
  const dataPointSummary = matching?.dataPointSummary || {};
  const categories = useMemo(() => matching?.microPointCategories || [], [matching?.microPointCategories]);
  const microPoints = useMemo(() => matching?.microPoints || [], [matching?.microPoints]);
  const dataPoints = matching?.dataPoints || [];
  const activePolicyKey = active?.id ?? active?.versionLabel ?? 'default';

  useEffect(() => {
    if (!active) return;
    const nextDraft = buildDraft(active, matching || {});
    const nextComparable = serializeDraftForCompare(nextDraft);
    const currentSource = draftSourceRef.current;
    const activePolicyChanged = currentSource?.key !== undefined && currentSource.key !== activePolicyKey;
    const hasLocalDraftChanges = Boolean(
      draft &&
      currentSource?.comparable &&
      serializeDraftForCompare(draft) !== currentSource.comparable
    );

    if (hasLocalDraftChanges && activePolicyChanged && !saving) {
      setPolicyConflict(true);
      toast.error('The active matching policy changed while this draft has unsaved edits. Reset the draft before saving.');
      return;
    }

    draftSourceRef.current = { key: activePolicyKey, comparable: nextComparable };
    setPolicyConflict(false);
    setDraft(nextDraft);
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const baselineDraft = useMemo(() => buildDraft(active, matching || {}), [active, matching]);
  const engineControlDefinitions = active?.engineControlDefinitions || matching?.definitions?.engineControls || fallbackEngineControls;
  const semanticDefinitions = active?.semanticSections || matching?.definitions?.semanticSections || fallbackSemanticSections;
  const rrfDefinitions = active?.rrfChannels || matching?.definitions?.rankFusion || fallbackRrfChannels;
  const dimensions = active?.dimensions || [];

  const totalDimensionPercent = useMemo(() => (
    Object.values(draft?.dimensionWeights || {}).reduce((sum, value) => sum + toNumber(value), 0)
  ), [draft]);
  const semanticTotal = useMemo(() => (
    Object.values(draft?.semanticSectionWeights || {}).reduce((sum, value) => sum + toNumber(value), 0)
  ), [draft]);
  const rrfTotal = useMemo(() => (
    Object.values(draft?.rrfWeights || {}).reduce((sum, value) => sum + toNumber(value), 0)
  ), [draft]);

  const totalValid = Math.abs(totalDimensionPercent - 100) <= 0.1;
  const semanticValid = Math.abs(semanticTotal - 100) <= 0.1;
  const rrfValid = Math.abs(rrfTotal - 100) <= 0.1;
  const hasPolicyChanges = draft && serializeDraftForCompare(draft) !== serializeDraftForCompare(baselineDraft);
  const draftVersionLabelValid = isVersionLabelValid(draft?.versionLabel);
  const changeReasonLength = draft?.changeReason?.trim().length || 0;
  const saveBlockers = useMemo(() => {
    const blockers = [];

    if (!hasPolicyChanges) blockers.push('Make a policy change before saving.');
    if (!totalValid) blockers.push(`Dimension weights must total 100%. Current total: ${totalDimensionPercent.toFixed(1)}%.`);
    if (!semanticValid) blockers.push(`Semantic section weights must total 100%. Current total: ${semanticTotal.toFixed(1)}%.`);
    if (!rrfValid) blockers.push(`Rank fusion weights must total 100%. Current total: ${rrfTotal.toFixed(1)}%.`);
    if (changeReasonLength < 20) blockers.push(`Add an audit reason with at least 20 characters. Current length: ${changeReasonLength}.`);
    if (!draftVersionLabelValid) blockers.push('Fix the optional version label format.');
    if (policyConflict) blockers.push('Reset the draft because the active policy changed while you were editing.');

    return blockers;
  }, [
    changeReasonLength,
    draftVersionLabelValid,
    hasPolicyChanges,
    policyConflict,
    rrfTotal,
    rrfValid,
    semanticTotal,
    semanticValid,
    totalDimensionPercent,
    totalValid
  ]);
  const canSave = saveBlockers.length === 0 && !saving;

  const filteredMicroPoints = useMemo(() => {
    const query = microSearch.trim().toLowerCase();
    return microPoints.filter((point) => {
      const matchesCategory = microCategory === 'all' || point.category === microCategory;
      const searchable = `${point.key} ${point.displayName} ${point.description} ${point.category}`.toLowerCase();
      return matchesCategory && (!query || searchable.includes(query));
    });
  }, [microPoints, microSearch, microCategory]);

  const microTotalPages = Math.max(1, Math.ceil(filteredMicroPoints.length / microPageSize));
  const paginatedMicroPoints = useMemo(() => {
    const start = microPage * microPageSize;
    return filteredMicroPoints.slice(start, start + microPageSize);
  }, [filteredMicroPoints, microPage, microPageSize]);

  useEffect(() => {
    setMicroPage(0);
  }, [microSearch, microCategory, microPageSize]);

  useEffect(() => {
    if (microPage > microTotalPages - 1) setMicroPage(Math.max(0, microTotalPages - 1));
  }, [microPage, microTotalPages]);

  useEffect(() => {
    if (!hasPolicyChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPolicyChanges]);

  const updateDimension = (payloadKey, value) => {
    setDraft((current) => ({
      ...current,
      dimensionWeights: {
        ...(current?.dimensionWeights || {}),
        [payloadKey]: clamp(value, 0, 100)
      }
    }));
  };

  const updateEngineControl = (definition, value) => {
    setDraft((current) => ({
      ...current,
      engineControls: {
        ...(current?.engineControls || {}),
        [definition.key]: clamp(value, definition.min, definition.max)
      }
    }));
  };

  const updateSemanticWeight = (key, value) => {
    setDraft((current) => ({
      ...current,
      semanticSectionWeights: {
        ...(current?.semanticSectionWeights || {}),
        [key]: clamp(value, 0, 100)
      }
    }));
  };

  const updateRrfWeight = (key, value) => {
    setDraft((current) => ({
      ...current,
      rrfWeights: {
        ...(current?.rrfWeights || {}),
        [key]: clamp(value, 0, 100)
      }
    }));
  };

  const updateCategory = (key, value) => {
    setDraft((current) => ({
      ...current,
      categoryWeights: {
        ...(current?.categoryWeights || {}),
        [key]: {
          enabled: value.enabled !== false,
          weight: clamp(value.weight, 0, 200)
        }
      }
    }));
  };

  const updateMicroPoint = (key, value) => {
    setDraft((current) => ({
      ...current,
      microPointWeights: {
        ...(current?.microPointWeights || {}),
        [key]: {
          enabled: value.enabled !== false,
          weight: clamp(value.weight, 0, 100),
          minimumScore: value.minimumScore === null || value.minimumScore === undefined ? null : clamp(value.minimumScore, 0, 100),
          mandatory: value.mandatory === true
        }
      }
    }));
  };

  const refresh = async () => {
    await queryClient.invalidateQueries('admin-configurations');
  };

  const handleSave = async () => {
    if (saveBlockers.length) {
      toast.error(saveBlockers[0]);
      return;
    }
    try {
      setSaving(true);
      await adminAPI.updateGlobalMatchingPolicy({
        dimensionWeights: draft.dimensionWeights,
        engineControls: draft.engineControls,
        semanticSectionWeights: draft.semanticSectionWeights,
        rrfWeights: draft.rrfWeights,
        categoryWeights: draft.categoryWeights,
        microPointWeights: draft.microPointWeights,
        changeReason: draft.changeReason.trim(),
        ...(draft.versionLabel.trim() ? { versionLabel: draft.versionLabel.trim() } : {})
      });
      toast.success('Global matching policy updated.');
      await refresh();
    } catch (error) {
      toast.error(error?.message || 'Global matching policy could not be updated.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (payload) => {
    if (!restoreConfig) return;
    try {
      setRestoring(true);
      await adminAPI.restoreGlobalMatchingPolicy(restoreConfig.id, payload);
      toast.success('Global matching policy restored.');
      setRestoreConfig(null);
      await refresh();
    } catch (error) {
      toast.error(error?.message || 'Global matching policy could not be restored.');
    } finally {
      setRestoring(false);
    }
  };

  const resetDraftToActive = () => {
    const nextDraft = buildDraft(active, matching || {});
    draftSourceRef.current = {
      key: activePolicyKey,
      comparable: serializeDraftForCompare(nextDraft)
    };
    setPolicyConflict(false);
    setDraft(nextDraft);
  };

  const sectionCounts = {
    matching: dimensions.length + engineControlDefinitions.length,
    microPoints: microPoints.length,
    data: dataPoints.length,
    compliance: (kyc.regions || []).length + (taxonomy.activeMicroPoints || microPointSummary.activeMicroPoints || 0),
    history: history.length
  };

  if (configQuery.isLoading && !draft) {
    return <ConfigurationsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <PageHeader
          title="Configurations"
          description="Manage matching, retrieval, micro-point, data-source, KYC, and taxonomy configuration with version history and audit reasons."
        />

        {configQuery.error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            {configQuery.error.message || 'Configurations could not be loaded.'}
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
          <Metric icon={GitBranch} label="Active policy" value={active?.versionLabel || 'Default'} detail={`${usage.activeEmployerOverrides || 0} employer overrides · Config ID ${active?.id || 'virtual default'}`} infoKey="versioning" onInfo={setInfoItem} />
          <Metric icon={Target} label="Dimension total" value={`${totalDimensionPercent.toFixed(1)}%`} detail={totalValid ? 'Ready to save' : 'Must equal 100%'} infoKey="dimensionTotal" onInfo={setInfoItem} tone={totalValid ? 'default' : 'danger'} />
          <Metric icon={Layers} label="Semantic total" value={`${semanticTotal.toFixed(1)}%`} detail={semanticValid ? 'Retrieval balanced' : 'Must equal 100%'} infoKey="semanticSectionWeights" onInfo={setInfoItem} tone={semanticValid ? 'default' : 'danger'} />
          <Metric icon={SlidersHorizontal} label="RRF total" value={`${rrfTotal.toFixed(1)}%`} detail={rrfValid ? 'Rank fusion balanced' : 'Must equal 100%'} infoKey="rrfWeights" onInfo={setInfoItem} tone={rrfValid ? 'default' : 'danger'} />
          <Metric icon={Tags} label="Micro-points" value={microPointSummary.microPoints || microPoints.length || 0} detail={`${microPointSummary.disabledByPolicy || 0} disabled by policy`} infoKey="microPointWeights" onInfo={setInfoItem} />
          <Metric icon={Database} label="Data sources" value={dataPointSummary.availableSources ?? dataPoints.length ?? 0} detail={`${dataPointSummary.totalRecords || 0} source records`} infoKey="dataPoints" onInfo={setInfoItem} />
        </section>

        <SectionNav activeSection={activeSection} onChange={setActiveSection} counts={sectionCounts} />

        {activeSection === 'matching' && (
          <section className="space-y-6">
            <SectionIntro
              icon={SlidersHorizontal}
              title="Global Matching Policy"
              subtitle="Default scoring, retrieval, semantic, and rank-fusion controls used by matching engines unless an employer-level or job-level override is active."
              action={(
                <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                  <InfoButton itemKey="versioning" onOpen={setInfoItem} />
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                    Active: {active?.versionLabel || 'Default'}
                  </span>
                </div>
              )}
            />

            <Panel
              icon={SlidersHorizontal}
              title="Weights and Retrieval Controls"
              subtitle="Change broad scoring weights first, then tune retrieval controls only when data quality and monitoring support the shift."
            >
              <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>These settings can alter ranking behavior across the platform. Save only with a clear operational reason and after confirming all required totals are exactly 100%.</p>
                </div>
              </div>
              {policyConflict && (
                <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-700">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>The active policy changed after this draft started. Reset the draft to the latest active policy before saving.</p>
                  </div>
                </div>
              )}

              <div className="space-y-5">
                <TotalStatus label="Dimension weights" value={totalDimensionPercent} valid={totalValid} infoKey="dimensionTotal" onInfo={setInfoItem} />
                <div className="space-y-3">
                  {dimensions.map((dimension) => (
                    <NumberControl
                      key={dimension.key}
                      label={dimension.label}
                      description={dimension.description}
                      value={draft?.dimensionWeights?.[dimension.payloadKey] ?? dimension.weightPercent}
                      min={0}
                      max={100}
                      step={0.5}
                      suffix="%"
                      infoKey={dimension.payloadKey}
                      onChange={(value) => updateDimension(dimension.payloadKey, value)}
                      onInfo={setInfoItem}
                    />
                  ))}
                </div>

                <div>
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]">
                    Ranking and retrieval controls
                    <InfoButton itemKey="vectorWeight" onOpen={setInfoItem} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {engineControlDefinitions.map((definition) => (
                      <NumberControl
                        key={definition.key}
                        label={definition.label}
                        description={definition.description}
                        value={draft?.engineControls?.[definition.key] ?? definition.defaultValue}
                        min={definition.min}
                        max={definition.max}
                        step={definition.step || 1}
                        suffix={definition.unit === 'percent' ? '%' : definition.unit === 'points' ? ' pts' : ''}
                        infoKey={definition.key}
                        onChange={(value) => updateEngineControl(definition, value)}
                        onInfo={setInfoItem}
                        soft
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <TotalStatus label="Semantic section weights" value={semanticTotal} valid={semanticValid} infoKey="semanticSectionWeights" onInfo={setInfoItem} />
                  <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {semanticDefinitions.map((section) => (
                      <NumberControl
                        key={section.key}
                        label={section.label}
                        description={section.description}
                        value={draft?.semanticSectionWeights?.[section.key] ?? section.weightPercent ?? section.defaultPercent}
                        min={0}
                        max={100}
                        step={0.5}
                        suffix="%"
                        infoItem={{
                          title: section.label,
                          summary: section.description,
                          impact: 'This controls the relative contribution of this profile or job section inside semantic retrieval.',
                          guidance: 'Keep the section family balanced at 100% and align changes with extraction quality.',
                          risk: 'Over-weighting one section can make semantic retrieval ignore stronger evidence in other sections.'
                        }}
                        onChange={(value) => updateSemanticWeight(section.key, value)}
                        onInfo={setInfoItem}
                        soft
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <TotalStatus label="Rank fusion weights" value={rrfTotal} valid={rrfValid} infoKey="rrfWeights" onInfo={setInfoItem} />
                  <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {rrfDefinitions.map((channel) => (
                      <NumberControl
                        key={channel.key}
                        label={channel.label}
                        description={channel.description}
                        value={draft?.rrfWeights?.[channel.key] ?? channel.weightPercent ?? channel.defaultPercent}
                        min={0}
                        max={100}
                        step={0.5}
                        suffix="%"
                        infoItem={{
                          title: channel.label,
                          summary: channel.description,
                          impact: 'This controls how much this rank source contributes to reciprocal rank fusion.',
                          guidance: 'Keep all rank fusion channels at 100% total and monitor ordering changes after release.',
                          risk: 'Over-weighting one rank source can suppress useful signals from the others.'
                        }}
                        onChange={(value) => updateRrfWeight(channel.key, value)}
                        onInfo={setInfoItem}
                        soft
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-[#0b1f3a]">Release Metadata</h3>
                    <p className="text-xs leading-5 text-slate-500">Every production policy change requires an audit reason before it can be saved.</p>
                  </div>
                  {hasPolicyChanges && <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">Draft has unsaved changes</span>}
                </div>
                {hasPolicyChanges && saveBlockers.length > 0 && (
                  <div id="policy-save-blockers" className="mb-4 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">
                    <p className="font-semibold text-slate-900">Save Policy is locked until these checks pass:</p>
                    <ul className="mt-2 space-y-1">
                      {saveBlockers.map((blocker) => (
                        <li key={blocker} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-950" />
                          <span>{blocker}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Version label <span className="font-normal text-slate-400">(optional)</span>
                    <input
                      value={draft?.versionLabel || ''}
                      onChange={(event) => setDraft((current) => ({ ...current, versionLabel: event.target.value }))}
                      placeholder="global-matching-v2"
                      aria-invalid={!draftVersionLabelValid}
                      className="mt-2 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    />
                    {!draftVersionLabelValid && (
                      <span className="mt-1 block text-xs font-normal text-rose-600">
                        Use 4-80 characters. Start with a letter or number; only dot, underscore, colon, and hyphen are allowed.
                      </span>
                    )}
                  </label>
                  <label className="block text-sm font-semibold text-slate-700 lg:row-span-2">
                    Change reason
                    <textarea
                      value={draft?.changeReason || ''}
                      onChange={(event) => setDraft((current) => ({ ...current, changeReason: event.target.value }))}
                      rows={5}
                      maxLength={1200}
                      placeholder="Explain the operational reason, expected impact, and monitoring plan for this change."
                      className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    />
                    {hasPolicyChanges && draft?.changeReason?.trim().length < 20 && (
                      <span className="mt-1 block text-xs font-normal text-rose-600">Enter at least 20 characters so the audit trail explains the operational reason.</span>
                    )}
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={resetDraftToActive}
                      disabled={!hasPolicyChanges || saving}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RotateCcw size={16} /> Reset draft
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={!canSave}
                      aria-describedby={hasPolicyChanges && saveBlockers.length > 0 ? 'policy-save-blockers' : undefined}
                      title={saveBlockers[0] || 'Save policy'}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-semibold text-white hover:bg-[#12345c] disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                      Save Policy
                    </button>
                  </div>
                </div>
              </div>
            </Panel>
          </section>
        )}

        {activeSection === 'microPoints' && (
          <section className="space-y-6">
            <SectionIntro
              icon={Tags}
              title="Micro-point Policy"
              subtitle="Category multipliers and individual evidence controls used by job micro-requirement extraction and deterministic scoring."
              action={<span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">{filteredMicroPoints.length} filtered</span>}
            />

            <Panel
              icon={Tags}
              title="Micro-point Controls"
              subtitle="Use filters and pagination for large taxonomies. Policy edits remain part of the same versioned global configuration."
              action={<InfoButton itemKey="microPointWeights" onOpen={setInfoItem} />}
            >
              <div className="mb-5 grid grid-cols-2 gap-3 text-center text-xs sm:grid-cols-5">
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{microPointSummary.categories || categories.length || 0}</strong>Categories</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{microPointSummary.activeMicroPoints || 0}</strong>Active points</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{microPointSummary.disabledByPolicy || 0}</strong>Disabled</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{microPointSummary.jobRequirements || 0}</strong>Job uses</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{microPointSummary.candidateEvidence || 0}</strong>Candidate facts</span>
              </div>

              <div className="mb-6">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]">
                  Category multipliers
                  <InfoButton itemKey="microPointCategories" onOpen={setInfoItem} />
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {categories.map((category) => (
                    <CategoryControl
                      key={category.key}
                      category={category}
                      value={draft?.categoryWeights?.[category.key] || { enabled: true, weight: 100 }}
                      onChange={(value) => updateCategory(category.key, value)}
                      onInfo={setInfoItem}
                    />
                  ))}
                </div>
                {!categories.length && <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No micro-point categories were reported by the backend.</p>}
              </div>

              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]">
                  Individual micro-points
                  <InfoButton itemKey="microPointWeights" onOpen={setInfoItem} />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,260px)_220px]">
                  <label className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      value={microSearch}
                      onChange={(event) => setMicroSearch(event.target.value)}
                      placeholder="Search micro-points"
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    />
                  </label>
                  <label className="relative">
                    <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <select
                      value={microCategory}
                      onChange={(event) => setMicroCategory(event.target.value)}
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    >
                      <option value="all">All categories</option>
                      {categories.map((category) => <option key={category.key} value={category.key}>{category.label || category.key}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <div className="space-y-3 p-3">
                  {paginatedMicroPoints.map((point) => (
                    <MicroPointControl
                      key={point.key}
                      point={point}
                      value={draft?.microPointWeights?.[point.key] || {
                        enabled: point.enabled !== false,
                        weight: point.weightPercent ?? point.defaultWeightPercent ?? 10,
                        minimumScore: point.minimumScore ?? null,
                        mandatory: point.mandatory === true
                      }}
                      onChange={(value) => updateMicroPoint(point.key, value)}
                      onInfo={setInfoItem}
                    />
                  ))}
                  {!filteredMicroPoints.length && (
                    <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">No micro-points match the selected filters.</p>
                  )}
                </div>
                {filteredMicroPoints.length > 0 && (
                  <PaginationControls
                    page={microPage}
                    pageSize={microPageSize}
                    totalItems={filteredMicroPoints.length}
                    onPageChange={setMicroPage}
                    onPageSizeChange={setMicroPageSize}
                  />
                )}
              </div>
            </Panel>
          </section>
        )}

        {activeSection === 'data' && (
          <section className="space-y-6">
            <SectionIntro
              icon={Database}
              title="Matching Data Inventory"
              subtitle="Source evidence families used by matching, scoring, retrieval, and audit explanations. This view is optimized as a scan table so large inventories do not collapse into uneven cards."
            />

            <Panel
              icon={Database}
              title="Data-point Coverage"
              subtitle="Review which data sources are configured, available, and measurable before increasing dependent scoring weights."
              action={<InfoButton itemKey="dataPoints" onOpen={setInfoItem} />}
            >
              <div className="mb-4 grid grid-cols-1 gap-3 text-center text-xs sm:grid-cols-3">
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{dataPointSummary.configuredSources || dataPoints.length || 0}</strong>Configured</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{dataPointSummary.availableSources ?? 0}</strong>Available</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600"><strong className="block text-xl text-[#0b1f3a]">{dataPointSummary.totalRecords || 0}</strong>Records</span>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="hidden grid-cols-[minmax(220px,1.25fr)_minmax(160px,0.7fr)_minmax(220px,1fr)_140px] gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 lg:grid">
                  <span>Evidence family</span>
                  <span>Area</span>
                  <span>Source</span>
                  <span className="text-right">Records</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {dataPoints.map((point) => (
                    <div key={point.key} className="grid gap-3 p-4 lg:grid-cols-[minmax(220px,1.25fr)_minmax(160px,0.7fr)_minmax(220px,1fr)_140px] lg:items-center lg:gap-4">
                      <div>
                        <p className="text-sm font-semibold text-[#0b1f3a]">{point.label}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">{point.description}</p>
                      </div>
                      <div>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{point.area}</span>
                      </div>
                      <div className="text-xs leading-5 text-slate-500">
                        <p className="font-medium text-slate-700">{point.sourceTable}</p>
                        <p className="break-all text-slate-400">{point.configPath}</p>
                      </div>
                      <div className="flex lg:justify-end">
                        <span className={`rounded-lg px-3 py-2 text-sm font-semibold ${point.recordCount === null ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'}`}>
                          {point.recordCount === null ? 'Unavailable' : `${point.recordCount} rows`}
                        </span>
                      </div>
                    </div>
                  ))}
                  {!dataPoints.length && <p className="p-4 text-sm text-slate-500">No data-point inventory was reported by the backend.</p>}
                </div>
              </div>
            </Panel>
          </section>
        )}

        {activeSection === 'compliance' && (
          <section className="space-y-6">
            <SectionIntro
              icon={ClipboardCheck}
              title="KYC and Taxonomy"
              subtitle="Regional verification requirements and canonical taxonomy health are grouped here so compliance operators are not forced into matching-policy controls."
            />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Panel
                icon={ClipboardCheck}
                title="Regional KYC Configuration"
                subtitle="Document requirements by employer verification region."
                action={<InfoButton itemKey="kycPolicy" onOpen={setInfoItem} />}
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(kyc.regions || []).map((region) => (
                    <div key={region.id || region.regionCode} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#0b1f3a]">{region.regionName}</p>
                          <p className="mt-1 text-xs text-slate-500">{region.regionCode} · Policy v{region.policyVersion}</p>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${region.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {region.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                        <span className="rounded-md bg-white p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{region.requiredDocuments}</strong>Required</span>
                        <span className="rounded-md bg-white p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{region.optionalDocuments}</strong>Optional</span>
                        <span className="rounded-md bg-white p-2 text-slate-600"><strong className="block text-[#0b1f3a]">{region.inactiveDocuments}</strong>Inactive</span>
                      </div>
                    </div>
                  ))}
                  {!(kyc.regions || []).length && (
                    <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 sm:col-span-2">No regional KYC policies were reported by the backend.</p>
                  )}
                </div>
                <a href="/kyc" className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Manage regional documents <ArrowRight size={15} />
                </a>
              </Panel>

              <Panel
                icon={Tags}
                title="Taxonomy Configuration"
                subtitle="Canonical tags and micro-point definitions used by extraction and matching."
                action={<InfoButton itemKey="taxonomy" onOpen={setInfoItem} />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="text-2xl font-semibold text-[#0b1f3a]">{taxonomy.pendingSuggestedTags || 0}</div>
                    <p className="mt-1 text-xs text-slate-500">Pending suggestions</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="text-2xl font-semibold text-[#0b1f3a]">{taxonomy.acceptedSuggestedTags || 0}</div>
                    <p className="mt-1 text-xs text-slate-500">Accepted suggestions</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="text-2xl font-semibold text-[#0b1f3a]">{taxonomy.activeMicroPoints || microPointSummary.activeMicroPoints || 0}</div>
                    <p className="mt-1 text-xs text-slate-500">Active micro-points</p>
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Sparkles size={16} className="text-slate-900" />
                    Active taxonomy
                  </div>
                  {taxonomy.activeTaxonomyVersion ? (
                    <p>{taxonomy.activeTaxonomyVersion.versionLabel} activated {formatDateTime(taxonomy.activeTaxonomyVersion.activatedAt)}.</p>
                  ) : (
                    <p>No active taxonomy version was reported by the backend.</p>
                  )}
                </div>
              </Panel>
            </div>
          </section>
        )}

        {activeSection === 'history' && (
          <section className="space-y-6">
            <SectionIntro
              icon={History}
              title="Version History and Guardrails"
              subtitle="Recent policy versions are displayed as a scalable recovery grid, with restore safeguards and operational constraints kept visible."
            />

            <Panel icon={History} title="Version History" subtitle="Recent global matching policy versions.">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {history.map((config) => {
                  const restoreTotalValid = isPolicyTotalValid(config);
                  const equivalentToActive = active && serializePolicyForCompare(config) === serializePolicyForCompare(active);
                  let restoreDisabledReason = '';
                  if (!config.isActive && !restoreTotalValid) {
                    restoreDisabledReason = `Cannot restore: dimension total is ${toNumber(config.totalDimensionPercent).toFixed(1)}%.`;
                  } else if (!config.isActive && equivalentToActive) {
                    restoreDisabledReason = 'Cannot restore: this version has the same effective weights as the active policy.';
                  }

                  return (
                    <div key={config.id || config.versionLabel} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-semibold text-[#0b1f3a]">{config.versionLabel}</p>
                            {config.isActive && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span>}
                            {!restoreTotalValid && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Invalid total</span>}
                          </div>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{formatDateTime(config.createdAt)}</p>
                          {config.createdByName && <p className="mt-1 truncate text-xs text-slate-400">{config.createdByName}</p>}
                        </div>
                        {!config.isActive && (
                          <button
                            type="button"
                            onClick={() => setRestoreConfig(config)}
                            disabled={Boolean(restoreDisabledReason)}
                            title={restoreDisabledReason || 'Restore this global matching policy'}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <RotateCcw size={13} /> Restore
                          </button>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-500">
                        <span>Skills {config.dimensions?.find((item) => item.key === 'skills')?.weightPercent ?? 0}%</span>
                        <span>Vector {config.engineControls?.vectorWeight ?? 0}%</span>
                        <span>LLM {config.engineControls?.llmMaxAdjustment ?? 0}</span>
                      </div>
                      {restoreDisabledReason && <p className="mt-2 text-xs leading-5 text-rose-600">{restoreDisabledReason}</p>}
                    </div>
                  );
                })}
                {!history.length && <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">No global matching policy versions were found.</p>}
              </div>
            </Panel>

            <Panel icon={ShieldCheck} title="Operational Guardrails" subtitle="Controls that reduce configuration risk.">
              <div className="grid gap-3 text-sm leading-6 text-slate-600 md:grid-cols-2">
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <p>Only admins with <span className="font-semibold text-[#0b1f3a]">config.manage</span> can save global policies.</p>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <p>Every save creates a new active version and logs the admin, reason, IP address, and user agent.</p>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <p>Dimension, semantic, and rank-fusion totals are validated before save.</p>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <p>Micro-point category and point overrides are versioned with the same production policy row.</p>
                </div>
              </div>
            </Panel>
          </section>
        )}

        {hasPolicyChanges && (
          <div className="fixed bottom-4 left-4 right-4 z-30 mx-auto flex max-w-[1400px] flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.12)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              {policyConflict ? <AlertTriangle size={16} className="text-rose-600" /> : <Zap size={16} className="text-slate-900" />}
              {policyConflict ? 'Active policy changed; reset draft before saving' : 'Unsaved global matching policy changes'}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {policyConflict && (
                <button
                  type="button"
                  onClick={resetDraftToActive}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <RotateCcw size={15} /> Reset draft
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-semibold text-white hover:bg-[#12345c] disabled:opacity-50"
              >
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                Save policy
              </button>
            </div>
          </div>
        )}
      </div>

      <InfoModal item={infoItem} onClose={() => setInfoItem(null)} />
      <RestoreModal
        config={restoreConfig}
        saving={restoring}
        onClose={() => {
          if (!restoring) setRestoreConfig(null);
        }}
        onConfirm={handleRestore}
      />
    </div>
  );
}

export default ConfigurationsPage;
